import React, { useEffect, useState, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import { useAuth } from './contexts/AuthContext';
import { auth, db, initializeDatabase } from './firebase';
import { Container, CircularProgress, ThemeProvider, createTheme, CssBaseline } from '@mui/material';

// Import pages directly
import SignupPage from './pages/SignupPage';
import VisitorQRRegistration from './pages/VisitorQRRegistration';
import LoginPage from './pages/LoginPage';
import VisitorDashboard from './pages/dashboards/VisitorDashboard';
import HostVisitHistory from './pages/HostVisitHistory';
import VisitorForm from './pages/VisitorRegistration';
import VisitorList from './pages/VisitorList';
import VisitApprovals from './pages/VisitApprovals';
import UserManagement from './pages/UserManagement';
import TenantManagement from './pages/TenantManagement';
import VisitorCheckoutQR from './components/VisitorCheckoutQR';
import VisitorCheckout from './pages/VisitorCheckout';
import UserProfile from './pages/UserProfile';
import QRScanner from './components/QRScanner';
import VisitorCheckoutSelect from './pages/VisitorCheckoutSelect';
import Footer from './components/Footer';
import NoticeManagement from './pages/NoticeManagement';
import NoticeBoard from './pages/NoticeBoard';
import Templates from "./pages/Templates";
import HomePage from "./pages/HomePage";
import SuperAdminDashboard from "./pages/dashboards/SuperAdminDashboard";
import TenantAdminDashboard from "./pages/dashboards/TenantAdminDashboard";
import FacilityAdminDashboard from "./pages/dashboards/FacilityAdminDashboard";
import HostDashboard from './pages/dashboards/HostDashboard';
import AppOperatorDashboard from "./pages/dashboards/AppOperatorDashboard";

function HostVisitHistoryWrapper() {
  const { user } = useAuth();
  if (!user) return null;
  return <HostVisitHistory operatorTenantId={user.tenantId} />;
}

function App() {
  const { isAuthenticated, user, loading, isSuperAdmin, isAppAdmin } = useAuth();
  const [darkMode, setDarkMode] = useState(false);
  const location = useLocation();

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: { main: '#1976d2' },
      secondary: { main: '#dc004e' },
    },
  });

  const toggleDarkMode = () => {
    setDarkMode(prevMode => !prevMode);
  };

  useEffect(() => {
    const initDb = async () => {
      try {
        console.log("Initializing database on app startup...");
        await initializeDatabase();
      } catch (error) {
        console.error("Error initializing database:", error);
      }
    };
    initDb();
  }, []);

  const getRoleBasedPath = (role) => {

    if (isSuperAdmin()) return '/superadmin-dashboard';

    if (role === "appadmin") return "/tenant-admin-dashboard";

    if (role === "facilityadmin") return "/facility-dashboard";

    if (role === "host" || role === "admin") return "/host-dashboard";

    if (role === "appoperator") return "/app-operator-dashboard";

    if (role === "visitor") return "/visitor-dashboard";

    return "/role-selection";
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <>
        {isAuthenticated && user && location.pathname !== "/" && (
          <Navbar />
        )}
        <Suspense fallback={
          <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
            <CircularProgress />
          </Container>
        }>
          <Routes>
            {/* Visitor QR Registration (public, no auth required) */}
            <Route path="/visitor-qr-registration" element={<VisitorQRRegistration />} />
            {/* Visitor QR Registration (public, no auth required) */}
            <Route path="/visitor-qr-registration" element={<VisitorQRRegistration />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/login" element={<LoginPage />} />

            {/* Visitor checkout route - requires authentication and visitor verification */}
            <Route element={<ProtectedRoute requireVisitorVerification={true} />}>
              <Route path="/visitor-checkout" element={<VisitorCheckout />} />
            </Route>

            {/* Visitor and AppOperator shared routes */}
            <Route element={<ProtectedRoute requiredRole={["visitor", "appoperator"]} />}>
              <Route path="/visitor-checkout" element={<VisitorCheckout />} />
              <Route path="/qr-scanner" element={<QRScanner />} />
              <Route path="/visitor-dashboard" element={<VisitorDashboard />} />
              <Route path="/visitor-registration" element={<VisitorForm />} />
              <Route path="/visitor-list" element={<VisitorList />} />
              <Route path="/select-visit" element={<VisitorCheckoutSelect />} />
            </Route>

            {/* Host and AppOperator routes for Checkout QR */}
            <Route element={<ProtectedRoute requiredRole={["host", "appoperator"]} />}>
              <Route path="/visitor-checkout-qr" element={<VisitorCheckoutQR />} />
            </Route>

            {/* Super Admin routes */}
            <Route element={<ProtectedRoute requiredRole="superadmin" />}>
              <Route path="/superadmin-dashboard" element={<SuperAdminDashboard />} />
            </Route>

            <Route element={<ProtectedRoute requiredRole="appadmin" />}>
              <Route path="/tenant-admin-dashboard" element={<TenantAdminDashboard />} />
            </Route>

            {/* Facility Admin Dashboard - accessible by facilityadmin AND appadmin with viewFacilityId */}
            <Route element={<ProtectedRoute requiredRole={["facilityadmin", "appadmin"]} />}>
              <Route path="/facility-dashboard" element={<FacilityAdminDashboard />} />
            </Route>

            {/* Host routes */}
            <Route element={<ProtectedRoute requiredRole="host" />}>
              <Route path="/host-dashboard" element={<HostDashboard />} />
              <Route path="/visit-approvals" element={<VisitApprovals />} />
              <Route path="/host/visits" element={<HostVisitHistory />} />
              <Route path="/tenant-dashboard" element={<Navigate to="/host-dashboard" replace />} />
              <Route path="/tenant/visits" element={<Navigate to="/host/visits" replace />} />
            </Route>

            {/* AppOperator routes */}
            <Route element={<ProtectedRoute requiredRole="appoperator" />}>
              <Route path="/app-operator-dashboard" element={<AppOperatorDashboard />} />
              <Route path="/app-operator/visits" element={<HostVisitHistoryWrapper />} />
            </Route>

            {/* Notice Management (AppAdmin, FacilityAdmin, AppOperator) */}
            <Route element={<ProtectedRoute requiredRole={["appadmin", "facilityadmin", "appoperator"]} />}>
              <Route path="/notice-management" element={<NoticeManagement />} />
            </Route>

            {/* Notice Board (All readers) */}
            <Route element={<ProtectedRoute requiredRole={["appadmin", "facilityadmin", "appoperator", "host"]} />}>
              <Route path="/notice-board" element={<NoticeBoard />} />
            </Route>

            <Route element={<ProtectedRoute requiredRole={["appadmin", "facilityadmin", "host", "appoperator"]} />}>
              <Route path="/templates" element={<Templates />} />
            </Route>

            {/* Admin routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/user-management" element={<UserManagement />} />
              <Route path="/host-management" element={<TenantManagement />} />
              <Route path="/tenant-management" element={<Navigate to="/host-management" replace />} />
              <Route path="/profile" element={<UserProfile />} />
            </Route>

            {/* Root redirect */}
            {/* <Route path="/" element={
              isAuthenticated
                ? <Navigate to={getRoleBasedPath(user?.role)} replace />
                : <HomePage />
            } /> */}
            <Route path="/" element={<HomePage />} />

            {/* Catch all */}
            <Route path="*" element={
              <Navigate to={isAuthenticated ? getRoleBasedPath(user?.role) : '/signup'} replace />
            } />
          </Routes>
        </Suspense>
        <Footer />
      </>
    </ThemeProvider>
  );
}

export default App;
